<template>
  <ElDialog
    width="30%"
    class="publish-request-modal"
    :title="$t('APP.ACTIONS.PUBLISH_REQUEST_TITLE')"
    :visible="value"
    @close="resetAndClose"
  >
    <span v-if="!canRequestAppReview">
      {{ unverifiedReviewRequestDesc }}
    </span>

    <template v-else>
      <span v-dompurify-html="$t('APP.ACTIONS.PUBLISH_REQUEST_DESC')" />

      <DangerousPermissions :permissions="dangerousPermissions" />

      <ElInput
        type="textarea"
        :value="note"
        :autosize="{ minRows: 2, maxRows: 4 }"
        :placeholder="$t('APP.RELEASES.DEVELOPER_NOTE')"
        @input="updateNote"
      />

      <div class="publish-request-modal__uploader">
        <DroppableFileSelector
          :accept="acceptedFileTypes"
          inline
          @change="setFile"
        >
          <div v-if="file" class="publish-request-modal__file-details">
            <FileUploadDetails :file-name="file.name" />
            <i
              class="publish-request-modal__remove-button el-icon-close"
              @click="removeFile"
            />
          </div>
        </DroppableFileSelector>
      </div>

      <div slot="footer" class="dialog-footer">
        <ElButton
          type="primary"
          native-type="submit"
          size="large"
          :loading="loading"
          @click.native.prevent="emitConfirm"
        >
          {{ $t('APP.ACTIONS.PUBLISH_REQUEST_TITLE') }}
        </ElButton>

        <ElButton
          type="text"
          :plain="true"
          @click.native.prevent="resetAndClose"
        >
          {{ $t('CANCEL') }}
        </ElButton>
      </div>
    </template>
  </ElDialog>
</template>

<script>
import DroppableFileSelector from '@@/common/file-selectors/DroppableFileSelector';
import FileUploadDetails from '@@/common/FileUploadDetails';
import DangerousPermissions from './DangerousPermissions';
import { convertArrayOfObjectToHashTable } from '@/utils/arrayToHashTable';

export default {
  name: 'PublishRequestModal',

  components: {
    DroppableFileSelector,
    FileUploadDetails,
    DangerousPermissions,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    note: {
      type: String,
      default: '',
    },
    file: {
      type: File,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    release: {
      type: Object,
      default: null,
    },
    app: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      errors: null,
    };
  },

  computed: {
    acceptedFileTypes() {
      return ['.jpeg', '.jpg', '.png', '.pdf'].join(',');
    },
    canRequestAppReview() {
      // TODO: this should be improved
      const isFreeApp = this.app.price === 0 && !this.app.haveIap;
      const isVerifiedForFree =
        this.verificationState === 'J' || this.verificationState === 'R';
      return this.verificationState === 'A' || (isFreeApp && isVerifiedForFree);
    },
    unverifiedReviewRequestDesc() {
      return this.$t(
        `APP.ACTIONS.UNVERIFIED_ACCOUNT_${this.verificationState}_DESC`,
      );
    },
    verificationState() {
      return this.$auth.user.publisher?.verificationState;
    },
    uploaderPlaceholder() {
      return this.file
        ? this.$t('APP.ACTIONS.PUBLISH_REQUEST_DESC.UPLOAD_FILE_EDIT')
        : this.$t('APP.ACTIONS.PUBLISH_REQUEST_DESC.UPLOAD_FILE');
    },
    dangerousPermissions() {
      if (!this.release) return;

      const packages = this.release.packages;
      const allDangerousPermissions = packages
        .map(pkg => pkg.dangerousPermissions || [])
        .flat();

      const uniqeDangerousPermissions = convertArrayOfObjectToHashTable(
        allDangerousPermissions,
        i => i.permissionName,
      );

      return Object.values(uniqeDangerousPermissions);
    },
  },

  methods: {
    emitConfirm() {
      this.$emit('confirm');
      this.resetAndClose();
    },
    setFile(files) {
      this.$emit('update:file', files[0]);
    },
    removeFile() {
      this.$emit('update:file', null);
    },
    updateNote(value) {
      this.$emit('update:note', value);
    },
    resetAndClose() {
      this.errors = null;
      this.removeFile();
      this.updateNote('');
      this.$emit('input', false);
    },
  },
};
</script>

<style lang="scss">
.publish-request-modal {
  &__uploader {
    margin-top: $global-margin;
  }

  &__file-details {
    display: flex;
    align-items: center;
  }

  &__remove-button {
    color: $lb-color-alert;
    margin-right: $global-margin * 0.3;
    margin-top: $global-margin * 0.5;
    padding: $global-padding * 0.2;
    cursor: pointer;

    &:hover {
      opacity: 0.6;
    }
  }
}
</style>
